type EasingFn = (t: number) => number;

export type ScrollOptions = {
  container?: HTMLElement | Window;
  duration?: number;
  offset?: number;
  easing?: EasingFn;
  block?: "start" | "center" | "end" | "nearest";
};

const easeInOutCubic: EasingFn = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

function getScrollTop(el: HTMLElement | Window) {
  return el === window
    ? window.scrollY || document.documentElement.scrollTop
    : (el as HTMLElement).scrollTop;
}

function setScrollTop(el: HTMLElement | Window, value: number) {
  if (el === window) {
    window.scrollTo(0, value);
  } else {
    (el as HTMLElement).scrollTop = value;
  }
}

function getMaxScroll(el: HTMLElement | Window) {
  if (el === window) {
    const doc = document.documentElement;
    const body = document.body;
    const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight);
    const maxScroll = scrollHeight - window.innerHeight;
    return maxScroll;
  }
  const node = el as HTMLElement;
  const maxScroll = node.scrollHeight - node.clientHeight;
  return maxScroll;
}

function getElementTopWithinContainer(
  target: Element,
  container: HTMLElement | Window,
): number {
  const targetRect = target.getBoundingClientRect();

  if (container === window) {
    return targetRect.top + getScrollTop(window);
  }

  const containerRect = (container as HTMLElement).getBoundingClientRect();
  return targetRect.top - containerRect.top + getScrollTop(container);
}

export function scrollIntoView(
  target: Element,
  {
    container = window,
    duration = 600,
    offset = 0,
    easing = easeInOutCubic,
    block = "center",
  }: ScrollOptions = {},
): Promise<void> {
  return new Promise((resolve) => {
    // Respect reduced motion
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reduceMotion.matches || duration <= 0) {
      // Fallback to instant jump with chosen block
      const topBase = getElementTopWithinContainer(target, container);
      const targetTop =
        block === "start"
          ? topBase
          : block === "end"
            ? topBase -
              (container === window
                ? window.innerHeight
                : (container as HTMLElement).clientHeight) +
              (target as HTMLElement).clientHeight
            : // "center" or "nearest"
              topBase -
              (container === window
                ? window.innerHeight
                : (container as HTMLElement).clientHeight) /
                2 +
              (target as HTMLElement).clientHeight / 2;

      const max = getMaxScroll(container);
      setScrollTop(container, Math.max(0, Math.min(targetTop + offset, max)));
      resolve();
      return;
    }

    const startY = getScrollTop(container);

    // Compute ideal align position similar to block:center/start/end
    const containerHeight =
      container === window
        ? window.innerHeight
        : (container as HTMLElement).clientHeight;
    const targetBaseTop = getElementTopWithinContainer(target, container);
    let desiredTop: number;

    if (block === "start") {
      desiredTop = targetBaseTop;
    } else if (block === "end") {
      desiredTop =
        targetBaseTop - containerHeight + (target as HTMLElement).clientHeight;
    } else {
      desiredTop =
        targetBaseTop -
        containerHeight / 2 +
        (target as HTMLElement).clientHeight / 2;
    }

    const max = getMaxScroll(container);
    const endY = Math.max(0, Math.min(desiredTop + offset, max));
    const distance = endY - startY;

    if (Math.abs(distance) < 1) {
      resolve();
      return;
    }

    let rafId = 0;
    const startTime = performance.now();

    // Cancel on user interaction
    const cancel = () => {
      if (rafId) cancelAnimationFrame(rafId);
      cleanup();
      resolve();
    };
    const onWheel = cancel;
    const onTouch = cancel;
    const onKey = (e: KeyboardEvent) => {
      // common scroll/keyboard interactions
      const keys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];
      if (keys.includes(e.key)) cancel();
    };

    const containerNode =
      container === window ? document : (container as HTMLElement);
    containerNode.addEventListener("wheel", onWheel, {
      passive: true,
      once: true,
    });
    containerNode.addEventListener("touchstart", onTouch, {
      passive: true,
      once: true,
    });
    window.addEventListener("keydown", onKey, { once: true });

    const cleanup = () => {
      containerNode.removeEventListener("wheel", onWheel);
      containerNode.removeEventListener("touchstart", onTouch);
      window.removeEventListener("keydown", onKey);
    };

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easing(t);
      const newScrollTop = startY + distance * eased;
      setScrollTop(container, newScrollTop);

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        cleanup();
        resolve();
      }
    };

    rafId = requestAnimationFrame(tick);
  });
}
